/**
 * Sort options config list – same pattern as Facet (Shopify-style).
 * List shows label only (read-only). "+ Add Sort" and "Edit" open a modal. No searchable/collapsible/type (facet-only).
 * Each item: { value: "field:asc" | "field:desc", label: "Display label" }
 */
import React, { useState } from "react";
import Select from "react-select";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EDIT_ICON = (
	<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32" aria-hidden="true">
		<path d="M30.276 1.722C29.168.612 27.69 0 26.12 0s-3.044.61-4.153 1.72L4.294 19.29c-.105.105-.185.23-.235.368l-4 11c-.13.355-.047.756.214 1.03C.466 31.892.73 32 1 32c.098 0 .196-.014.293-.044l9.95-3.052c.155-.047.297-.133.413-.248l18.62-18.62C31.39 8.925 32 7.447 32 5.877c0-1.57-.61-3.046-1.724-4.156zM10.092 27.165L6.368 28.31c-.217-.638-.555-1.202-1.016-1.663-.4-.4-.866-.71-1.356-.96L5.7 21H8v2c0 .553.447 1 1 1h1.765l-.673 3.165zm14.72-14.494L12.628 24.856l.35-1.647c.062-.296-.012-.603-.202-.837-.19-.234-.475-.37-.776-.37h-2v-2c0-.552-.448-1-1-1H7.422L19.315 7.175l.012.01c.732-.732 1.707-1.135 2.742-1.135s2.01.403 2.74 1.136 1.14 1.707 1.14 2.743c0 1.035-.404 2.008-1.138 2.74zm4.05-4.05l-.932.934c-.09-1.43-.683-2.76-1.703-3.782-1.02-1.022-2.354-1.614-3.787-1.703l.938-.932.002-.002C24.11 2.403 25.085 2 26.12 2s2.01.403 2.742 1.136C29.596 3.87 30 4.843 30 5.878c0 1.037-.402 2.01-1.138 2.743zm-6.57-.327l-10 10c-.39.39-.39 1.023 0 1.414.195.195.452.293.708.293s.51-.098.707-.293l10-10c.39-.39.39-1.023 0-1.414-.392-.39-1.023-.39-1.414 0z" fillRule="evenodd" />
	</svg>
);
const REMOVE_ICON = (
	<svg viewBox="0 0 20 20" width="16" height="16" focusable="false" aria-hidden="true">
		<path d="M11.414 10l4.293-4.293a.999.999 0 1 0-1.414-1.414L10 8.586 5.707 4.293a.999.999 0 1 0-1.414 1.414L8.586 10l-4.293 4.293a.999.999 0 1 0 1.414 1.414L10 11.414l4.293 4.293a.997.997 0 0 0 1.414 0 .999.999 0 0 0 0-1.414L11.414 10z" fillRule="evenodd" />
	</svg>
);

const DRAG_HANDLE = (
	<svg width="7" height="11" viewBox="0 0 7 11" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
		<g fill="#D3DBE2">
			<circle cx="1.5" cy="1.5" r="1.5" />
			<circle cx="5.5" cy="1.5" r="1.5" />
			<circle cx="1.5" cy="5.5" r="1.5" />
			<circle cx="5.5" cy="5.5" r="1.5" />
			<circle cx="1.5" cy="9.5" r="1.5" />
			<circle cx="5.5" cy="9.5" r="1.5" />
		</g>
	</svg>
);

function parseSortValue(value) {
	if (!value || typeof value !== "string") return { field: "", direction: "asc" };
	const parts = value.split(":");
	return { field: parts[0] || "", direction: parts[1] === "desc" ? "desc" : "asc" };
}

function SortableSortRow({ index, item, onEdit, onRemove }) {
	const id = String(index);
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const displayLabel = item.label || parseSortValue(item.value).field || "—";

	return (
		<li className="cbSortListItem">
			<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
				<div className="sortableLeft">
					{DRAG_HANDLE}
					<span className="mb-0" style={{ color: "inherit" }}>{displayLabel}</span>
				</div>
			</div>
			<div className="sortableRight">
				<Button type="button" variant="utility" onClick={(e) => { e.preventDefault(); onEdit(index); }}>
					{EDIT_ICON}
					Edit
				</Button>
				<Button type="button" variant="utility" onClick={(e) => { e.preventDefault(); onRemove(index); }}>
					{REMOVE_ICON}
					Remove
				</Button>
			</div>
		</li>
	);
}

export default function SortConfigList({ sortOrders, onChange, fieldOptions, schemaLoading }) {
	const sorted = [...(sortOrders || [])];
	const fieldNames = Array.isArray(fieldOptions) ? fieldOptions : [];
	const hasFieldOptions = fieldNames.length > 0;
	const selectOptions = fieldNames.map((name) => ({ value: name, label: name }));

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	);

	const [modalOpen, setModalOpen] = useState(false);
	const [editIndex, setEditIndex] = useState(null);
	const [newField, setNewField] = useState("");
	const [newDirection, setNewDirection] = useState("asc");
	const [newLabel, setNewLabel] = useState("");

	const isEditMode = editIndex != null;

	function remove(i) {
		onChange(sorted.filter((_, idx) => idx !== i));
	}

	function handleDragEnd(ev) {
		const { active, over } = ev;
		if (over == null || active.id === over.id) return;
		const oldIndex = sorted.findIndex((_, i) => String(i) === active.id);
		const newIndex = sorted.findIndex((_, i) => String(i) === over.id);
		if (oldIndex === -1 || newIndex === -1) return;
		onChange(arrayMove(sorted, oldIndex, newIndex));
	}

	function openAddModal() {
		setEditIndex(null);
		setNewField("");
		setNewDirection("asc");
		setNewLabel("");
		setModalOpen(true);
	}

	function openEditModal(i) {
		const item = sorted[i] || {};
		const { field, direction } = parseSortValue(item.value);
		setEditIndex(i);
		setNewField(field);
		setNewDirection(direction);
		setNewLabel(item.label || "");
		setModalOpen(true);
	}

	function closeModal() {
		setModalOpen(false);
		setEditIndex(null);
	}

	function saveModal() {
		if (!newField) return;
		const value = newField + ":" + newDirection;
		const label = newLabel || newField + (newDirection === "asc" ? " (A–Z / Low–High)" : " (Z–A / High–Low)");
		if (isEditMode) {
			const next = sorted.map((s, idx) => (idx === editIndex ? { value, label } : s));
			onChange(next);
		} else {
			onChange([...sorted, { value, label }]);
		}
		closeModal();
	}

	const sortableIds = sorted.map((_, i) => String(i));

	return (
		<div>
			<p className="text-muted small mb-2">Use drag and drop to reorder. Click Edit to change label and options.</p>
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
					<ul className="cbSortList">
						{sorted.map((s, i) => (
							<SortableSortRow
								key={i}
								index={i}
								item={s}
								onEdit={openEditModal}
								onRemove={remove}
							/>
						))}
					</ul>
				</SortableContext>
			</DndContext>
			<button type="button" className="btn btn-outline-primary btn-sm mt-3" onClick={openAddModal}>
				+ Add Sort
			</button>

			<Modal isOpen={modalOpen} toggle={closeModal} centered>
				<ModalHeader style={{ backgroundColor: "transparent", color: "#333" }} toggle={closeModal}>
					{isEditMode ? "Edit sort order" : "Add sort order"}
				</ModalHeader>
				<ModalBody>
					<Form>
						<FormGroup>
							<Label for="sortField">Field</Label>
							<div style={{ minWidth: "200px" }}>
								<Select
									inputId="sortField"
									options={selectOptions}
									value={hasFieldOptions && newField ? { value: newField, label: newField } : null}
									onChange={(opt) => setNewField(opt ? opt.value : "")}
									placeholder={schemaLoading ? "Loading..." : hasFieldOptions ? "Select field..." : "Select index first"}
									isDisabled={!hasFieldOptions || isEditMode}
									isClearable={!isEditMode}
									noOptionsMessage={() => "No sortable fields"}
								/>
							</div>
							{isEditMode && <small className="text-muted">Field cannot be changed when editing.</small>}
						</FormGroup>
						<FormGroup>
							<Label for="sortDirection">Direction</Label>
							<Input
								id="sortDirection"
								type="select"
								value={newDirection}
								onChange={(e) => setNewDirection(e.target.value)}
							>
								<option value="asc">Asc</option>
								<option value="desc">Desc</option>
							</Input>
						</FormGroup>
						<FormGroup>
							<Label for="sortLabel">Display label</Label>
							<Input
								id="sortLabel"
								type="text"
								placeholder="e.g. Price: Low to High"
								value={newLabel}
								onChange={(e) => setNewLabel(e.target.value)}
								autoComplete="off"
							/>
						</FormGroup>
					</Form>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={closeModal}>Cancel</Button>
					<Button color="primary" onClick={saveModal} disabled={!hasFieldOptions || !newField}>
						{isEditMode ? "Save" : "Add"}
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
}
